import { Box, MenuItem, Select, Stack, Typography } from "@mui/material";
import { grey } from "@mui/material/colors";
import Dialog from "@mui/material/Dialog";
import Button from "@mui/material/Button";
import DialogActions from "@mui/material/DialogActions";
import { useEffect, useState, useMemo } from "react";
import { constraints, datatypes, inputTypes } from "data/globalData";
import { attrGrpAttrAssocObj, attrValAssocObj } from "components/PimDashboard/DashboardComponents/Designer/DesignerAttributes/data";
import {
	attrConditionalDisplayColObj,
	attrGrpTableStrColumnsObj,
	formatColumnValue,
	getColDefaultValue,
} from "components/PimDashboard/data";
import RenderAttributeInputs, {
	removeDropdownOptionsBasedonDependency,
} from "components/PimDashboard/DashboardComponents/Designer/CreateStyle/RenderAttributes/RenderAttributeInputs";
import { PimDashboardState } from "context/PimDashboardContext";
import { ExpandLess, ExpandMore } from "@mui/icons-material";
import RenderStyleInputField from "components/PimDashboard/DashboardComponents/Designer/CreateStyle/RenderStyleInputFieldNU/RenderStyleInputField";

const splitAttributesByCondition = (columns, condAttr, condAttrColkey, dataObj) => {
	if (!condAttr) return { arr1: columns, arr2: [] };

	const condAttrColVal = dataObj?.[condAttr?.linked_table]?.[condAttrColkey];
	if (!condAttrColVal || condAttrColVal === getColDefaultValue(condAttr)) {
		return { arr1: columns, arr2: [] };
	}

	const currentCondValue = dataObj?.[condAttr.linked_table]?.[condAttr.key];
	let a1 = [],
		a2 = [];

	columns?.forEach((col) => {
		const attrDisplayTrigger = col?.data?.[attrConditionalDisplayColObj.key];
		const hasDisplayTrigger = Array.isArray(attrDisplayTrigger) && attrDisplayTrigger.length > 0;

		if (!hasDisplayTrigger || attrDisplayTrigger.includes(currentCondValue)) {
			a1.push(col);
		} else {
			a2.push(col);
		}
	});

	return { arr1: a1, arr2: a2 };
};

const DisplayStyleDetailsWithEditTab = ({
	columns,
	data,
	width = "24%",
	longWidth = "49%",
	attrGrp,
	updateFunction,
	disableEdit = false,
	lockEdit = false,
	disabledColumns = [],
	pendingFields = [],
}) => {
	const { allAttributes } = PimDashboardState();
	const [modalOpen, setModalOpen] = useState(false);
	const [updatedData, setUpdatedData] = useState(data);
	const [errObj, setErrObj] = useState({});
	const [viewAll, setViewAll] = useState(false);
	const [modalViewAll, setModalViewAll] = useState(false);

	const condGrpAttrColkey = attrGrp?.data?.[attrConditionalDisplayColObj.key];
	const colCondAttr = useMemo(() => {
		if (!condGrpAttrColkey || !allAttributes) return null;
		const arr = allAttributes.filter((row) => row?.key === condGrpAttrColkey);
		return arr?.length ? arr[0] : null;
	}, [condGrpAttrColkey, allAttributes]);

	const { arr1, arr2 } = useMemo(
		() => splitAttributesByCondition(columns, colCondAttr, condGrpAttrColkey, data),
		[columns, colCondAttr, condGrpAttrColkey, data],
	);

	const { arr1: modalArr1, arr2: modalArr2 } = useMemo(
		() => splitAttributesByCondition(columns, colCondAttr, condGrpAttrColkey, updatedData),
		[columns, colCondAttr, condGrpAttrColkey, updatedData],
	);

	const pendingKeys = useMemo(() => new Set(pendingFields.map((row) => row.key)), [pendingFields]);

	useEffect(() => {
		setUpdatedData(data);
	}, [data]);

	const handleCloseModal = () => {
		setUpdatedData(data);
		setModalOpen(false);
	};

	const handleFormSubmit = (e) => {
		e.preventDefault();
		// Pass full style payload — attributes in a group can span multiple linked_tables
		// (e.g. style + style_details). Sending only attrGrp.linked_table drops other edits.
		if (typeof updateFunction === "function") updateFunction(updatedData, handleCloseModal, attrGrp);
	};

	const isColumnDisabled = (column) =>
		disabledColumns.includes(column.key) || column?.input_type === inputTypes.AUTO_GENERATE.key;

	const renderAttributeBox = (row) => {
		const isPending = pendingKeys.has(row?.key);
		return (
			<Box
				width={row.data_type === datatypes.LONGTEXT.key ? longWidth : width}
				key={row?.name}
				display="flex"
				border={`1px solid white`}
				boxSizing="border-box"
				pb={1}>
				<Box
					sx={{
						maxWidth: "38%",
						minWidth: "25%",
						borderRadius: "6px 0px 0px 6px",
						p: 1,
						background: isPending ? "#9f1239" : "#000",
						color: grey[50],
						display: "flex",
						alignItems: "center",
						p: "10px 2%",
					}}>
					<Typography variant="subtitle2" color={grey[200]} noWrap width="100%">
						{row?.name}
					</Typography>
				</Box>

				<Box
					sx={{
						width: "60%",
						flex: 1,
						background: grey[100],
						fontWeight: 400,
						overflow: "hidden",
						borderRadius: "0px 6px 6px 0px",
						display: "flex",
						alignItems: "center",
						p: "10px 2%",
					}}>
					<Typography
						variant="subtitle2"
						width="100%"
						sx={{
							fontWeight: 400,
							display: "-webkit-box",
							WebkitBoxOrient: "vertical",
							WebkitLineClamp: 3,
							overflow: "hidden",
						}}>
						{data?.[row.linked_table][row?.key] !== undefined
							? formatColumnValue(data?.[row.linked_table][row?.key], row)
							: ""}
					</Typography>
				</Box>
			</Box>
		);
	};

	return (
		<Box width="99%" display="flex" flexWrap="wrap" columnGap="1%">
			<Box width="100%" justifyContent="space-between" display="flex" alignItems="center" pt={2} pb={1}>
				<Typography variant="h6">
					{attrGrp[attrGrpTableStrColumnsObj.name.key]} {disableEdit && "Details"}
				</Typography>
				<Button
					variant="contained"
					onClick={() => setModalOpen(true)}
					size="small"
					sx={{ display: disableEdit || lockEdit ? "none" : "block" }}>
					Edit
				</Button>
			</Box>
			{arr1?.map(renderAttributeBox)}
			{arr2?.length > 0 && (
				<>
					<Box width="100%" display="flex" justifyContent="flex-end">
						<Button size="small" onClick={() => setViewAll(!viewAll)} endIcon={viewAll ? <ExpandLess /> : <ExpandMore />}>
							View {viewAll ? "Less" : "All"}
						</Button>
					</Box>
					{viewAll && arr2?.map(renderAttributeBox)}
				</>
			)}

			<Dialog
				fullWidth
				maxWidth={{ xs: "lg", xl: "xl" }}
				open={modalOpen}
				onClose={handleCloseModal}
				component="form"
				onSubmit={handleFormSubmit}>
				<Box width="98%" p={2} boxSizing="border-box">
					<Stack width="100%" justifyContent="space-between" flexWrap="wrap" rowGap={1}>
						<Stack direction="row" width="100%" flexWrap="wrap" columnGap="2%" rowGap={1}>
							<Typography variant="h6" width="100%" pl={1}>
								Edit {attrGrp[attrGrpTableStrColumnsObj.name.key]}
							</Typography>
							{colCondAttr ? (
								<>
									{modalArr1?.map((column) => {
										let options = removeDropdownOptionsBasedonDependency(
											column,
											updatedData,
											column?.[attrValAssocObj],
											allAttributes,
										);
										return (
											<RenderStyleInputField
												key={column.key}
												column={{ ...column, [attrValAssocObj]: options }}
												data={updatedData}
												setData={setUpdatedData}
												selectOptObjName={attrValAssocObj}
												width="32%"
												longWidth="48%"
												attrGrp={attrGrp}
												error={!!errObj?.[column.key]}
												errorObj={errObj}
												setError={setErrObj}
												disabled={isColumnDisabled(column)}
											/>
										);
									})}
									{modalArr2?.length > 0 && (
										<>
											<Box width="100%" display="flex" justifyContent="flex-end">
												<Button
													size="small"
													onClick={() => setModalViewAll(!modalViewAll)}
													endIcon={modalViewAll ? <ExpandLess /> : <ExpandMore />}>
													View {modalViewAll ? "Less" : "All"}
												</Button>
											</Box>
											{modalViewAll &&
												modalArr2?.map((column) => {
													let options = removeDropdownOptionsBasedonDependency(
														column,
														updatedData,
														column?.[attrValAssocObj],
														allAttributes,
													);
													return (
														<RenderStyleInputField
															key={column.key}
															column={{ ...column, [attrValAssocObj]: options }}
															data={updatedData}
															setData={setUpdatedData}
															selectOptObjName={attrValAssocObj}
															width="32%"
															longWidth="48%"
															attrGrp={attrGrp}
															error={!!errObj?.[column.key]}
															errorObj={errObj}
															setError={setErrObj}
															disabled={isColumnDisabled(column)}
														/>
													);
												})}
										</>
									)}
								</>
							) : (
								<RenderAttributeInputs
									columns={[...attrGrp[attrGrpAttrAssocObj]]}
									setData={setUpdatedData}
									data={updatedData}
									attrGrp={attrGrp}
									errObj={errObj}
									setErrorObj={setErrObj}
									disabledColumns={disabledColumns}
								/>
							)}
						</Stack>
					</Stack>
				</Box>
				<DialogActions>
					<Button variant="outlined" onClick={handleCloseModal}>
						Cancel
					</Button>
					<Button autoFocus variant="contained" sx={{ mr: 1 }} type="submit">
						Save
					</Button>
				</DialogActions>
			</Dialog>
		</Box>
	);
};

export default DisplayStyleDetailsWithEditTab;
